// ui/src/pages/calendar/AdminCalendar.tsx
//
// The admin/org-admin calendar (Task 20) — the richest of the three role
// views, and the only one where closures are MANAGED rather than merely
// observed. Renders the shared <CalendarGrid> in month mode (density: how
// many classes run/are suppressed that day) or week mode (per-class times,
// enrolled-vs-capacity, and closure state), plus a class filter and, for
// ORG_ADMIN/SUPER_ADMIN, a location switcher — same pattern Classes.tsx
// already uses for org-level location scoping.
//
// Closure management (declare / edit / remove / flip-to-open) lives in the
// sibling <ClosureDialog>, opened either from the top-level "Declare
// closure" button or by clicking a closed day.
import React, { useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useToast } from '../../components/Toast';
import CalendarGrid from '../../components/calendar/CalendarGrid';
import ClosureDialog from '../../components/calendar/ClosureDialog';
import type { CalendarDay, CalendarMode } from '../../components/calendar/types';
import {
  useGetCalendarQuery,
  useListClosuresQuery,
  useLocalGetLocationsQuery,
  useGetClassesQuery,
  useGetClassCapacityQuery,
} from '../../store/api/localApi';
import { ClosureStatus } from '../../types';

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Monday-first index (0-6, Mon=0), matching CalendarGrid's own convention. */
function mondayFirstIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** First/last day of the month containing `date`. */
function monthRange(date: Date): { from: string; to: string } {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toIsoDate(first), to: toIsoDate(last) };
}

/** Monday-to-Sunday week containing `date`. */
function weekRange(date: Date): { from: string; to: string } {
  const start = new Date(date);
  start.setDate(start.getDate() - mondayFirstIndex(date));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { from: toIsoDate(start), to: toIsoDate(end) };
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const AdminCalendar: React.FC = () => {
  const { isOrgLevel, activeOrganizationId } = useAuth();
  const { effectiveLocationId } = useLocationContext();
  const { showSuccess, showError } = useToast();

  const [mode, setMode] = useState<CalendarMode>('month');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [classFilter, setClassFilter] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>(effectiveLocationId ?? '');

  // Org admins pick a location; location admins are already scoped to one.
  const { data: locations } = useLocalGetLocationsQuery(activeOrganizationId || '', {
    skip: !isOrgLevel || !activeOrganizationId,
  });
  const locationId = isOrgLevel ? selectedLocationId : effectiveLocationId;

  const { data: classes } = useGetClassesQuery(locationId || '', {
    skip: !locationId,
  });

  const range = mode === 'month' ? monthRange(anchorDate) : weekRange(anchorDate);

  const {
    data: calendarData,
    isLoading: calendarLoading,
    isFetching: calendarFetching,
  } = useGetCalendarQuery(
    {
      from: range.from,
      to: range.to,
      locationId,
      classId: classFilter || undefined,
    },
    { skip: !locationId }
  );

  const { data: closuresData } = useListClosuresQuery(
    { locationId: locationId || '', from: range.from, to: range.to },
    { skip: !locationId }
  );

  const { data: capacity } = useGetClassCapacityQuery(classFilter, {
    skip: !classFilter,
  });
  const capacityByWeekday = useMemo(() => {
    const map = new Map<number, { enrolled: number; remaining: number }>();
    for (const row of capacity?.perDay ?? []) {
      map.set(row.day, { enrolled: row.enrolled, remaining: row.remaining });
    }
    return map;
  }, [capacity]);

  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureDialogDate, setClosureDialogDate] = useState<string | undefined>(undefined);
  const [editingClosureId, setEditingClosureId] = useState<string | undefined>(undefined);

  // Location-level closure coverage, keyed by date — independent of whether
  // any class is actually scheduled that weekday. A class-entry's own
  // `suppressedBy: 'CLOSURE'` (from calendarData) only exists for dates a
  // class runs on, so a closure landing on a day with zero scheduled classes
  // (a Saturday public holiday, say) would be invisible to the grid without
  // this: it's built from closuresData, which is a complete date range, not
  // gated on class schedules.
  const closureByDate = useMemo(() => {
    const map = new Map<string, { closureId: string; label: string }>();
    for (const closure of closuresData?.closures ?? []) {
      if (closure.status !== ClosureStatus.CLOSED) continue;
      let cursor = new Date(closure.startDate);
      const end = new Date(closure.endDate);
      while (cursor <= end) {
        map.set(toIsoDate(cursor), { closureId: closure.closureId, label: closure.label });
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [closuresData]);

  const closureIdByDate = useMemo(
    () => new Map([...closureByDate].map(([date, c]) => [date, c.closureId])),
    [closureByDate]
  );

  const days: CalendarDay[] = useMemo(
    () =>
      (calendarData?.days ?? []).map((day) => ({
        date: day.date,
        classes: day.classes.map((entry) => ({
          classId: entry.classId,
          className: entry.className,
          startTime: entry.startTime,
          endTime: entry.endTime,
          suppressedBy: entry.suppressedBy,
          closureLabel: entry.closureLabel,
          overrideReason: entry.overrideReason,
        })),
        closureLabel: closureByDate.get(day.date)?.label,
      })),
    [calendarData, closureByDate]
  );

  const dayByDate = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);

  function handleNavigate(direction: 'prev' | 'next' | 'today') {
    setAnchorDate((prev) => {
      if (direction === 'today') return new Date();
      const next = new Date(prev);
      if (mode === 'month') {
        next.setMonth(next.getMonth() + (direction === 'next' ? 1 : -1));
      } else {
        next.setDate(next.getDate() + (direction === 'next' ? 7 : -7));
      }
      return next;
    });
  }

  function handleDayClick(date: string) {
    const closureId = closureIdByDate.get(date);
    setClosureDialogDate(date);
    setEditingClosureId(closureId);
    setClosureDialogOpen(true);
  }

  function handleDeclareClick() {
    setClosureDialogDate(undefined);
    setEditingClosureId(undefined);
    setClosureDialogOpen(true);
  }

  function handleDialogClose() {
    setClosureDialogOpen(false);
    setClosureDialogDate(undefined);
    setEditingClosureId(undefined);
  }

  const weekDates = mode === 'week' ? days.map((d) => d.date) : [];

  return (
    <div className="py-6" data-testid="admin-calendar-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Calendar
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Class schedule, overrides and closures for this location
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              type="button"
              data-testid="closure-declare"
              onClick={handleDeclareClick}
              disabled={!locationId}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              Declare closure
            </button>
          </div>
        </div>

        {isOrgLevel && locations && locations.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              data-testid="calendar-location-switcher"
              value={selectedLocationId}
              onChange={(e) => {
                setSelectedLocationId(e.target.value);
                setClassFilter('');
              }}
              className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">Select a location...</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
          <select
            data-testid="calendar-class-filter"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            disabled={!locationId}
            className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="">All classes</option>
            {classes?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {locationId && closuresData && closuresData.closures.length > 0 && (
          <div className="mb-6 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700">Closures in view</h3>
            </div>
            <ul className="divide-y divide-gray-200">
              {closuresData.closures.map((closure) => (
                <li
                  key={closure.closureId}
                  className="px-4 py-3 flex items-center justify-between text-sm"
                >
                  <div>
                    <span className="font-medium text-gray-900">{closure.label}</span>
                    <span className="ml-2 text-gray-500">
                      {closure.startDate === closure.endDate
                        ? closure.startDate
                        : `${closure.startDate} – ${closure.endDate}`}
                    </span>
                    {closure.status === ClosureStatus.OPEN_AS_NORMAL && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        Open as normal
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid={`closure-edit-${closure.closureId}`}
                    onClick={() => {
                      setClosureDialogDate(closure.startDate);
                      setEditingClosureId(closure.closureId);
                      setClosureDialogOpen(true);
                    }}
                    className="text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Edit
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!locationId ? (
          <p className="text-sm text-gray-500">Select a location to see its calendar.</p>
        ) : calendarLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            <p className="mt-2 text-sm text-gray-500">Loading calendar...</p>
          </div>
        ) : (
          <>
            {/* Teach the hidden interaction once: the day cells open the closure
                dialog on click, which is otherwise not obvious (item 9). */}
            <p data-testid="calendar-closure-tip" className="mb-2 text-xs text-gray-500">
              Tip: click any day to declare a closure or mark it open.
            </p>
            <div
              data-testid="calendar-fetching"
              data-fetching={calendarFetching}
              className={calendarFetching ? 'opacity-60 transition-opacity' : ''}
            >
              <CalendarGrid
                mode={mode}
                days={days}
                onDayClick={handleDayClick}
                onNavigate={handleNavigate}
                onModeChange={setMode}
                dayAction={(isClosed) => ({
                  label: 'manage closure',
                  hoverCue: isClosed ? 'Edit closure' : 'Close day',
                })}
              />
            </div>

            {mode === 'week' && (
              <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Day
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Classes
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Enrolled / Capacity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {weekDates.map((date, idx) => {
                        const day = dayByDate.get(date);
                        const jsWeekday = new Date(date).getDay();
                        const occupancy = capacityByWeekday.get(jsWeekday);
                        return (
                          <tr key={date}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {WEEKDAY_NAMES[idx]} {date.slice(-2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {day && day.classes.length > 0 ? (
                                <ul className="space-y-1">
                                  {day.classes.map((entry) => (
                                    <li
                                      key={entry.classId}
                                      className={
                                        entry.suppressedBy
                                          ? 'text-slate-500 line-through'
                                          : undefined
                                      }
                                    >
                                      {entry.className} ({entry.startTime}-{entry.endTime})
                                      {entry.suppressedBy === 'CLOSURE' && entry.closureLabel && (
                                        <span className="ml-2 text-xs text-slate-500">
                                          {entry.closureLabel}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-gray-400">No classes</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {classFilter && occupancy ? (
                                <span
                                  data-testid={`calendar-occupancy-${classFilter}-${date}`}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700"
                                >
                                  {occupancy.enrolled} / {occupancy.enrolled + occupancy.remaining}
                                </span>
                              ) : (
                                <span className="text-gray-400">
                                  {classFilter ? 'Not running' : 'Select a class'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {locationId && (
        <ClosureDialog
          isOpen={closureDialogOpen}
          onClose={handleDialogClose}
          locationId={locationId}
          classes={classes ?? []}
          date={closureDialogDate}
          closureId={editingClosureId}
          existingClosure={
            editingClosureId
              ? closuresData?.closures.find((c) => c.closureId === editingClosureId)
              : undefined
          }
          onSuccess={(message) => showSuccess(message)}
          onError={(message) => showError(message)}
        />
      )}
    </div>
  );
};

export default AdminCalendar;
